import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans } from "@coworker/locales";
import React from "react";
import { useInputPopup } from "../../InputPopup";
import { getAssigneesPopup } from "../../TaskForm/common/popup.helpers";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import Quantity from "../../../assets/svg/quantity.svg?react";
import Location from "../../../assets/svg/location.svg?react";
import Other from "../../../assets/svg/issue_other.svg?react";
import closeReasonTypeOptions from "@coworker/enums/closeReasonTypes";
import { postToTestbuyService } from "../../../hooks/API/testbuy.service";
import { TestBuy } from "@coworker/types/lib/testbuy";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import CirclePathTwoTriangles from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CirclePathTwoTriangles";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import { FixaWindow } from "@coworker/reusable/helpers/devices";

export const testBuyCloseReasons = [
  {
    icon: Location,
    name: <Trans>tbCloseReasonHFB</Trans>,
    type: closeReasonTypeOptions.HFB,
  },
  {
    icon: Quantity,
    name: <Trans>tbCloseReasonAmount</Trans>,
    type: closeReasonTypeOptions.AMOUNT,
  },
  {
    icon: Other,
    name: <Trans>issueNameOtherString</Trans>,
    type: closeReasonTypeOptions.OTHER,
    descriptionRequired: true,
  },
];

interface PopupMenuProps {
  id: string | undefined;
}

interface ReassignResult {
  id: string;
}

export function useTestbuyOptionsMenu({ id }: PopupMenuProps): {
  getOptionsMenu: () => void;
  loading: boolean;
} {
  const { data: activeUsers, isLoading: activeUsersLoading } =
    useActiveStoreUsers();
  const { getInput } = useInputPopup();
  const navigate = useFixaNavigate();
  const { showToast } = useToastNotification();
  const [loading, setLoading] = React.useState(true);
  const [assigneePopUpRequested, setAssigneePopUpRequested] =
    React.useState(false);
  const [closeReasonPopUpRequested, setCloseReasonPopUpRequested] =
    React.useState(false);

  const handleAssigneeConfirmation = React.useCallback(async () => {
    const onAssigneeConfirmed = async (assignee: {
      gid: string;
      uid: string;
    }) => {
      const { enableUIDInTestbuy } = window as FixaWindow;

      const currentAssignedUser = activeUsers?.find(
        (user) => user.uid === assignee.uid || user.fixaUid === assignee.uid
      );

      // FixaUid -> uid migration: If no migration then uid is equal to fixaUid. If those are equal no migration will happen.
      const assignedUserIds = enableUIDInTestbuy
        ? {
            uid: currentAssignedUser?.uid,
            fixaUid: currentAssignedUser?.fixaUid,
          }
        : {
            uid: currentAssignedUser?.fixaUid,
            fixaUid: currentAssignedUser?.fixaUid,
          };

      if (!currentAssignedUser) return;

      setLoading(true);
      await postToTestbuyService(
        "testbuy/update_testbuy_assigned_to",
        { testbuyId: id, assignee: assignedUserIds.uid },
        (reassignResult: ReassignResult[]) => {
          if (reassignResult.length > 0) {
            setLoading(false);
            setAssigneePopUpRequested(false);
          }
        }
      );

      navigate("/testbuy");
      showToast(<Trans>tbReassigned</Trans>, undefined, undefined, 5);
    };

    // Triggering assignee popup request if needed
    if (assigneePopUpRequested) {
      setAssigneePopUpRequested(false);
      await getAssigneesPopup({
        getInput,
        onChange: onAssigneeConfirmed,
        lastAssignedTeamId: "",
        disableTeamAssignment: true,
      });
    }
  }, [assigneePopUpRequested, id, getInput, navigate, showToast, activeUsers]);

  React.useEffect(() => {
    if (!activeUsersLoading) setLoading(false);
    handleAssigneeConfirmation();
  }, [
    assigneePopUpRequested,
    getInput,
    id,
    navigate,
    showToast,
    handleAssigneeConfirmation,
    activeUsersLoading,
  ]);

  // Wrap openCloseReasonPopup in useCallback to avoid excessive re-renders
  const openCloseReasonPopup = React.useCallback(async () => {
    const closeReason = await getInput("selectIssue", {
      title: <Trans>whatString</Trans>,
      content: testBuyCloseReasons,
    });

    if (closeReason && id) {
      setLoading(true);
      postToTestbuyService(
        "testbuy/close_testbuy",
        {
          testbuyId: id,
          reasonType: closeReason.issue,
          ...(closeReason.description && {
            closeReasonDescription: closeReason.description,
          }),
        },
        () => {
          setLoading(false);
        }
      );

      // When transfer is completed, redirect back to TB list.
      navigate("/testbuy");
      showToast(<Trans>tbClosed</Trans>, undefined, undefined, 5);
    }
  }, [getInput, id, navigate, showToast]);

  React.useEffect(() => {
    if (closeReasonPopUpRequested) {
      setCloseReasonPopUpRequested(false);
      openCloseReasonPopup();
    }
  }, [closeReasonPopUpRequested, openCloseReasonPopup]);

  const actions = [
    {
      onClick: () => setAssigneePopUpRequested(true),
      title: <Trans>reassignString</Trans>,
      enum: "REASSIGN",
      icon: <FixaSSRIcon paths={CirclePathTwoTriangles} />,
    },
    {
      isHidden: false,
      isDisabled: false,
      onClick: () => setCloseReasonPopUpRequested(true),
      title: <Trans>closeActionString</Trans>,
      enum: "CLOSE",
      icon: <FixaSSRIcon paths={CrossIconPath} />,
    },
  ];
  const getOptionsMenu = () => getInput("popupMenu", { actions });

  return { getOptionsMenu, loading };
}
